import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchHealingHistory,
  fetchHealingStats,
  resolveHealingEvent,
  runHealAll,
} from '../../services/locatorDriftService';
import { errorMessage } from './locatorDriftUtils';

export const loadHealingAsync = createAsyncThunk(
  'locatorDrift/healing/load',
  async (options = {}, { getState, rejectWithValue }) => {
    try {
      const healing = getState().locatorDrift.healing;
      const historyOptions = {
        limit: options.limit ?? 50,
        sort: options.sort ?? healing.sort,
        hideLowSignal: options.hideLowSignal ?? healing.hideLowSignal,
      };
      const [stats, events] = await Promise.all([
        fetchHealingStats(),
        fetchHealingHistory(historyOptions),
      ]);
      return { stats, events, historyOptions };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const resolveHealingEventAsync = createAsyncThunk(
  'locatorDrift/healing/resolve',
  async ({ eventId, outcome }, { dispatch, rejectWithValue }) => {
    try {
      await resolveHealingEvent(eventId, outcome);
      await dispatch(loadHealingAsync());
      return { eventId, outcome };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const runHealAllAsync = createAsyncThunk(
  'locatorDrift/healing/runHealAll',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await runHealAll(payload);
      if (!payload.dry_run) {
        await dispatch(loadHealingAsync());
      }
      return {
        ...(response?.report || response),
        dry_run: payload.dry_run,
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

const healingSlice = createSlice({
  name: 'locatorDriftHealing',
  initialState: {
    stats: {},
    events: [],
    sort: 'score',
    hideLowSignal: true,
    loading: false,
    error: null,
    resolvingId: null,
    healRunLoading: false,
    healRunError: null,
    healRunReport: null,
  },
  reducers: {
    setHealingSort(state, action) {
      state.sort = action.payload;
    },
    setHealingHideLowSignal(state, action) {
      state.hideLowSignal = action.payload;
    },
    clearHealRunReport(state) {
      state.healRunReport = null;
      state.healRunError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadHealingAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadHealingAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats || {};
        state.events = Array.isArray(action.payload.events) ? action.payload.events : [];
        state.sort = action.payload.historyOptions?.sort ?? state.sort;
        state.hideLowSignal = action.payload.historyOptions?.hideLowSignal ?? state.hideLowSignal;
      })
      .addCase(loadHealingAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resolveHealingEventAsync.pending, (state, action) => {
        state.resolvingId = action.meta.arg.eventId;
      })
      .addCase(resolveHealingEventAsync.fulfilled, (state) => {
        state.resolvingId = null;
      })
      .addCase(resolveHealingEventAsync.rejected, (state, action) => {
        state.resolvingId = null;
        state.error = action.payload;
      })
      .addCase(runHealAllAsync.pending, (state) => {
        state.healRunLoading = true;
        state.healRunError = null;
      })
      .addCase(runHealAllAsync.fulfilled, (state, action) => {
        state.healRunLoading = false;
        state.healRunReport = action.payload || null;
      })
      .addCase(runHealAllAsync.rejected, (state, action) => {
        state.healRunLoading = false;
        state.healRunError = action.payload;
      });
  },
});

export const { clearHealRunReport, setHealingHideLowSignal, setHealingSort } = healingSlice.actions;

export default healingSlice.reducer;
